import AppOptions from "../app/options";
import Html from "./html";
import User from "./user";
import VideoPlaybackOptions from "./video_playback_options";

declare var google: any;
declare var shaka: any;

const VMAP_URLS: { [country: string]: string } = {
    si: 'https://avod.api.24ur.si/v1/r/vod/{MEDIA_ID}/{DEVICEID}?ppid={PPID}',
};

const AD_TIERS = ['start', 'start_y'];

export interface AdBreak {
    time: number;
    played: boolean;
}

class VoyoAds {
    private ad: any = null;
    private breaks: Array<AdBreak> = [];
    private session = 0;

    onAdPlayingChange: ((playing: boolean) => void) | null = null;

    onAdProgress: ((currentTime: number, duration: number) => void) | null = null;

    onAdBreaksChange: ((breaks: Array<AdBreak>) => void) | null = null;

    constructor(
        protected html: Html,
        protected user: User,
        protected options: AppOptions
    ) { }

    get isAdPlaying(): boolean {
        return !!this.ad;
    }

    get isAdPaused(): boolean {
        return !!this.ad?.isPaused();
    }

    shouldPlay(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions): boolean {
        return !!this.options.tiersEnabled &&
            AD_TIERS.includes(this.user.user?.tier || '') &&
            playbackOptions.type === 'video' &&
            isFinite(video.duration) &&
            !playbackOptions.isTrailer &&
            !playbackOptions.isErrorVideo &&
            !playbackOptions.paused;
    }

    /**
     * Requests VMAP for given media and starts playback: preroll first (if there is one), then content.
     * If anything goes wrong with ads, content is started without them.
     */
    play(shakaPlayer: any, video: HTMLMediaElement, mediaId: number, playbackOptions: VideoPlaybackOptions): Promise<void> {
        this.reset();
        const session = this.session;

        return this.html.loadVideoBannerElements()
            .then(() => {
                // User switched to another video while IMA was loading.
                if (session !== this.session) {
                    return;
                }

                if (typeof google === 'undefined' || !google.ima) {
                    throw new Error('IMA SDK is not loaded');
                }

                const adManager = shakaPlayer.getAdManager();
                const Events = shaka.ads.Utils;

                shakaPlayer.configure('ads.skipPlayDetection', true);

                adManager.setLocale(this.options.country === 'si' ? 'sl' : this.options.country);
                adManager.setContainers(this.getAdContainer(video), null);

                adManager.addEventListener(Events.IMA_AD_MANAGER_LOADED, (e: any) => {
                    // Some TV's do not support multiple working <video> at a time (isCustomPlaybackUsed === true)
                    // tell shaka to remove content from <video> when an ad is playing and reinsert it after ad ends.
                    if (e.imaAdManager.isCustomPlaybackUsed()) {
                        shakaPlayer.configure({
                            ads: {
                                supportsMultipleMediaElements: false,
                                customPlayheadTracker: true,
                            },
                        });
                        this.guardContentComplete();
                    }

                    const cuePoints: Array<number> = e.imaAdManager.getCuePoints();

                    this.setBreaks(cuePoints.filter(time => time > 0).map(time => ({ time, played: false })));

                    if (!cuePoints.includes(0)) {
                        video.play();
                    }
                });
                adManager.addEventListener(Events.AD_STARTED, (e: any) => {
                    // Shaka fires this also on CONTENT_PAUSE_REQUESTED, before IMA knows if break has any ad (it can be empty).
                    if (e.originalEvent?.type !== google.ima.AdEvent.Type.STARTED) {
                        return;
                    }

                    if (e.ad.isLinear()) {
                        this.setAd(e.ad);
                        this.setVolume(video.volume, this.isMuted(video));
                        this.markBreakPlayed(e.ad.getTimeOffset());
                    }
                });
                adManager.addEventListener(Events.AD_STOPPED, () => this.setAd(null));
                adManager.addEventListener(Events.AD_PAUSED, () => this.setPausedClass(true));
                adManager.addEventListener(Events.AD_RESUMED, () => this.setPausedClass(false));
                adManager.addEventListener(Events.AD_PROGRESS, (e: any) => this.onProgress(e.originalEvent?.getAdData?.()));
                adManager.addEventListener(Events.AD_ERROR, (e: any) => {
                    console.log('Ad error', e.originalEvent?.getError?.());
                    this.setAd(null);

                    if (video.paused && !video.ended) {
                        video.play();
                    }
                });

                const adsRequest = new google.ima.AdsRequest();
                adsRequest.adTagUrl = this.buildVmapUrl(mediaId);
                this.setPlaybackSignals(adsRequest, video, playbackOptions);

                adManager.requestClientSideAds(adsRequest);
            })
            .catch(err => {
                console.log('Ads error', err);
                if (session === this.session) {
                    video.play();
                }
            });
    }

    togglePause(): void {
        this.isAdPaused ? this.resume() : this.pause();
    }

    pause(): void {
        this.ad?.pause();
    }

    resume(): void {
        this.ad?.play();
    }

    setVolume(volume: number, muted: boolean): void {
        this.ad?.setVolume(volume);
        this.ad?.setMuted(muted);
    }

    reset(): void {
        this.session++;
        this.setAd(null);
        this.setBreaks([]);
    }

    private onProgress(data: any): void {
        if (!this.ad || !data || !(data.duration > 0) || !(data.currentTime >= 0)) {
            return;
        }

        this.onAdProgress?.(Math.min(data.currentTime, data.duration), data.duration);
    }

    private setBreaks(breaks: Array<AdBreak>): void {
        this.breaks = breaks;
        this.onAdBreaksChange?.(this.breaks);
    }

    private markBreakPlayed(timeOffset: number): void {
        const adBreak = this.breaks.find(b => !b.played && Math.abs(b.time - timeOffset) < 1);

        if (adBreak) {
            adBreak.played = true;
            this.onAdBreaksChange?.(this.breaks);
        }
    }

    private setPlaybackSignals(adsRequest: any, video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions): void {
        if (typeof playbackOptions.clickToPlay === 'boolean') {
            adsRequest.setAdWillAutoPlay(!playbackOptions.clickToPlay);
        }

        adsRequest.setAdWillPlayMuted(this.isMuted(video));
    }

    private isMuted(video: HTMLMediaElement): boolean {
        return video.muted || video.volume === 0;
    }

    private buildVmapUrl(mediaId: number): string {
        const user = this.user.user;
        const ppid = user?.id ? user.id + '-' + user.profileId : '';

        return VMAP_URLS[this.options.country]
            .replace('{MEDIA_ID}', encodeURIComponent(mediaId))
            .replace('{DEVICEID}', encodeURIComponent(this.user.deviceId || ''))
            .replace('{PPID}', encodeURIComponent(ppid));
    }

    private getAdContainer(video: HTMLMediaElement): HTMLElement {
        let container = this.html.q('.player_ads', video.parentElement);

        if (!container) {
            container = document.createElement('div');
            container.className = 'player_ads';
            video.parentElement?.appendChild(container);
        }

        return container;
    }

    /**
     * On TV's that use a single <video> element Shaka takes the ad ended event
     * and calls adsLoader.contentComplete(), which makes IMA drop all upcoming ads,
     * so we add a custom guard to prevent this, mark complete when the actual content ends.
     */
    private guardContentComplete(): void {
        const proto = google.ima.AdsLoader?.prototype;
        if (!proto || typeof proto.contentComplete !== 'function' || proto.voyoGuarded) {
            return;
        }

        const ads = this;
        const contentComplete = proto.contentComplete;

        proto.contentComplete = function () {
            if (ads.isAdPlaying) {
                return;
            }

            return contentComplete.apply(this, arguments);
        };
        proto.voyoGuarded = true;
    }

    private setAd(ad: any): void {
        const playing = !!ad;

        if (this.isAdPlaying !== playing) {
            this.onAdPlayingChange?.(playing);
        }

        this.ad = ad;

        if (playing) {
            this.html.addClass('body', 'ad_playing');
            this.setPausedClass(false);
        } else {
            this.html.removeClass('body', 'ad_playing');
        }
    }

    private setPausedClass(paused: boolean): void {
        this.html.removeClass('body', paused ? 'playing' : 'paused');
        this.html.addClass('body', paused ? 'paused' : 'playing');
    }
}

export default VoyoAds;
